import { ActivityStatusOverride, SubmissionStatus } from '@graphql/generated-graphql';
import { statusToCss, statusToIcon } from '@hooks/grading/useGradeSubmissions';
import * as Popover from '@radix-ui/react-popover';
import { Text } from '@skipper/ui';

const SortMap = {
    [SubmissionStatus.MarkedDone]: 1,
    [SubmissionStatus.Reassigned]: 2,
    [SubmissionStatus.Absent]: 3,
    [SubmissionStatus.Excused]: 4,
    [SubmissionStatus.Dropped]: 5,
    [SubmissionStatus.Skipped]: 6,
    [SubmissionStatus.Missed]: 7,
};

const ActivityOverrideStatuses = Object.keys(SubmissionStatus)
    .filter(
        (status) =>
            ![
                SubmissionStatus.NeedsGrading,
                SubmissionStatus.Completed,
                SubmissionStatus.Missed,
                SubmissionStatus.MarkedDone,
            ].includes(SubmissionStatus[status]),
    )
    .sort((statusA, statusB) => {
        let a = SortMap[SubmissionStatus[statusA]];
        let b = SortMap[SubmissionStatus[statusB]];
        if (!a) {
            a = 1000;
        }
        if (!b) {
            b = 1000;
        }

        return a - b;
    });

const ActivityOverrideStatusesWithMarkDone = Object.keys(SubmissionStatus)
    .filter(
        (status) =>
            ![
                SubmissionStatus.NeedsGrading,
                SubmissionStatus.Completed,
                SubmissionStatus.Missed,
            ].includes(SubmissionStatus[status]),
    )
    .sort((statusA, statusB) => {
        let a = SortMap[SubmissionStatus[statusA]];
        let b = SortMap[SubmissionStatus[statusB]];
        if (!a) {
            a = 1000;
        }
        if (!b) {
            b = 1000;
        }

        return a - b;
    });

export function StatusElementIcon({ status }: { status: SubmissionStatus }) {
    const Icon = statusToIcon(status);

    if (!Icon) return null;

    return (
        <span>
            <Icon className="w-5 h-5" />
        </span>
    );
}

export function StatusElementText({ status }: { status: SubmissionStatus }) {
    const cssStyle = statusToCss(status);
    switch (status) {
        case SubmissionStatus.NeedsGrading:
            return <Text css={{ ...cssStyle, justifyContent: 'start' }}>needs grading</Text>;
        case SubmissionStatus.MarkedDone:
            return <Text css={{ ...cssStyle, justifyContent: 'start' }}>marked done</Text>;
        default:
            return <Text css={{ ...cssStyle, justifyContent: 'start' }}>{status}</Text>;
    }
}

export function StatusElement({
    status,
    isLarge,
    gradedActivity = true,
}: {
    status: SubmissionStatus;
    isLarge?: boolean;
    gradedActivity?: boolean;
}) {
    const cssStyle = statusToCss(status, isLarge);

    if (!gradedActivity && status === SubmissionStatus.NeedsGrading)
        return <Text css={{ ...cssStyle, justifyContent: 'start' }}>Needs review</Text>;

    switch (status) {
        case SubmissionStatus.Completed:
            return null;
        case SubmissionStatus.NeedsGrading:
            return <Text css={{ ...cssStyle, justifyContent: 'start' }}>needs grading</Text>;
        case SubmissionStatus.MarkedDone:
            return (
                <Text css={{ ...cssStyle, justifyContent: 'start' }}>
                    <StatusElementIcon status={status} />
                    marked done
                </Text>
            );
        default:
            return (
                <Text css={{ ...cssStyle, justifyContent: 'start' }}>
                    <StatusElementIcon status={status} />
                    {status}
                </Text>
            );
    }
}

/**
 * Overflow Aware Menu
 *
 * @returns
 *
 */
export function ActivityStatusOverrideSelector({
    children,
    onSelection,
    open,
    setOpen,
    buttonId,
    onKeyDown,
    onCloseAutoFocus,
    canMarkDone = false,
}: {
    open: boolean;
    setOpen: (b: boolean) => void;
    children: React.ReactNode;
    onSelection: (status: ActivityStatusOverride | null) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
    buttonId?: string;
    onCloseAutoFocus?: (e: Event) => void;
    canMarkDone?: boolean;
}) {
    const activityStatuses = canMarkDone
        ? ActivityOverrideStatusesWithMarkDone
        : ActivityOverrideStatuses;
    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger
                id={buttonId}
                onKeyDown={onKeyDown}
                className="max-w-fit min-w-max focus:outline-none">
                {children}
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align="end"
                    sideOffset={12}
                    onCloseAutoFocus={(e) => {
                        e.preventDefault();
                    }}
                    onEscapeKeyDown={(e) => {
                        onCloseAutoFocus?.(e);
                    }}
                    className="bg-white p-2 rounded-md flex flex-col min-w-[166px] shadow">
                    <button
                        onClick={() => {
                            onSelection(null);
                            setOpen(false);
                        }}
                        className="px-2 py-1 focus:outline-[#01AAB9]">
                        <div className="w-full h-[28px] border-2 rounded-md border-dashed" />
                    </button>
                    {activityStatuses.map((status) => (
                        <button
                            key={status}
                            onClick={() => {
                                onSelection(ActivityStatusOverride[status]);
                                setOpen(false);
                            }}
                            className="px-2 py-1 focus:outline-[#01AAB9]">
                            <StatusElement key={status} status={SubmissionStatus[status]} />
                        </button>
                    ))}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
